import React, { useState, useRef } from "react";
import SEO from "../components/SEO";
import { contactPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { contactFaqs } from "../data/faqs";
import ReCaptcha from "../components/ReCaptcha";
import { sendContactEmail } from "../services/emailService";

/* ─── Success Modal ───────────────────────────────────────────── */
const SuccessModal = ({ name, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
    <div
      className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center z-10 animate-fade-in">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="w-20 h-20 bg-[#9b1c2e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
        Message Sent!
      </h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">
        Thank you, <strong className="text-gray-800">{name}</strong>! Your inquiry has been sent successfully. We will get back to you shortly.
      </p>

      <button
        onClick={onClose}
        className="w-full py-3 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-sm font-semibold rounded-full transition-colors"
      >
        Okay, Got It
      </button>
    </div>
  </div>
);

/* ─── Main Page ───────────────────────────────────────────────── */
const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
    agree: false,
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const captchaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setCaptchaError("Please complete the reCAPTCHA verification to submit your message.");
      return;
    }

    setIsSubmitting(true);
    try {
      await sendContactEmail(form, captchaToken);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to send contact email:", err);
      setCaptchaError("Failed to send message. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", mobile: "", message: "", agree: false });
    setCaptchaToken("");
    setCaptchaError("");
    captchaRef.current?.reset();
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-[#f9f5f0]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title="Contact Barbequeen Restaurant | Call, Visit or Book Now"
        description="Contact Barbequeen Restaurant for table reservations, feedback, catering, and franchise enquiries. Call, email, or visit our outlets across Tamil Nadu today."
        keywords="Contact Barbequeen, Barbequeen Salem contact, Barbequeen phone number, Barbequeen reservations, Barbequeen feedback"
        robots="index, follow, max-image-preview:large"
        canonical="https://barbequeen.com/contact-us"
        og={{
          type: "website",
          url: "https://barbequeen.com/contact-us",
          title: "Contact Barbequeen Restaurant | Call, Visit or Book Now",
          description: "Reach Barbequeen for reservations, feedback, catering & franchise enquiries. Call, email, or visit our outlets across Tamil Nadu.",
          image: "https://barbequeen.com/images/contact-og.jpg",
          imageWidth: "1200",
          imageHeight: "630",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        schema={contactPageSchema}
      />
      {showModal && <SuccessModal name={form.name} onClose={resetForm} />}

      {/* ── Hero Banner ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "340px" }}>
        <img
          src="https://barbequeen.com/public/uploads/04-14-2023-1681461453.jpg"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">Get In Touch</p>
          <h1 className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
            Contact Us
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            We are always here to listen. Speak to us about your experience or ask any questions.
          </p>
        </div>
      </section>

      {/* ── Content Grid ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* Left Column: Contact details */}
          <div className="lg:col-span-5 space-y-6">

            {/* Timing Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Our Timing</h3>
                <p className="text-sm font-semibold text-gray-700">Monday to Sunday</p>
                <p className="text-xs text-gray-500 mt-0.5">11.00 am - 11.00 pm</p>
              </div>
            </div>

            {/* Address Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>Contact Info</h3>
                <p className="text-xs text-gray-600 leading-relaxed font-medium">
                  Nonvee Foods Private Limited,<br />
                  # 32, Valmiki Street,<br />
                  Near Ramakrishna Higher Secondary School,<br />
                  Subramaniya Nagar,<br />
                  Salem – 636 005.
                </p>
              </div>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
              <h3 className="text-base font-bold text-gray-900 pb-2 border-b border-gray-100" style={{ fontFamily: "'Inter', sans-serif" }}>Quick Contact</h3>

              {/* Whatsapp */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 11.957.01c3.178 0 6.165 1.239 8.413 3.489 2.247 2.25 3.48 5.24 3.479 8.413-.004 6.607-5.341 11.947-11.902 11.947-2.008 0-3.98-.507-5.728-1.476L0 24zm6.59-4.846c1.666.988 3.3 1.493 5.3 1.495 5.51 0 9.99-4.48 9.993-9.99.002-2.67-1.037-5.18-2.92-7.07C17.136 1.7 14.624.66 11.96.66c-5.523 0-10.016 4.49-10.02 10.01-.002 2.083.548 4.115 1.593 5.923l-.993 3.626 3.71-.973-.203-.122zm10.87-7.4c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">Whatsapp Contact</p>
                  <a href="https://wa.me/919597228788" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-800 hover:text-[#9b1c2e] transition-colors">+91 95972-28788</a>
                </div>
              </div>

              {/* Call */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#9b1c2e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">Phone Number</p>
                  <a href="tel:+919597228788" className="text-sm font-bold text-gray-800 hover:text-[#9b1c2e] transition-colors">+91-95972-28788</a>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">Email Address</p>
                  <a href="mailto:feedback@barbequeen.com" className="text-sm font-bold text-[#9b1c2e] hover:underline">feedback@barbequeen.com</a>
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Message Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#0e0003] to-[#2a0008] px-8 py-10">
                <p className="text-xs font-bold tracking-[0.4em] uppercase text-[#c0424e] mb-2">Message Us</p>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>Send a Message</h2>
                <p className="text-gray-400 text-xs mt-2 leading-relaxed">
                  Have a question about our service? We're here to help, contact us today.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Your Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#9b1c2e] focus:ring-2 focus:ring-[#9b1c2e]/10 text-gray-800 text-sm bg-white transition-all placeholder-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Your Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#9b1c2e] focus:ring-2 focus:ring-[#9b1c2e]/10 text-gray-800 text-sm bg-white transition-all placeholder-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Your Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    placeholder="e.g. +91 XXXXX XXXXX"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#9b1c2e] focus:ring-2 focus:ring-[#9b1c2e]/10 text-gray-800 text-sm bg-white transition-all placeholder-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Enter Your Message Here *</label>
                  <textarea
                    rows={4}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us what you need help with..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#9b1c2e] focus:ring-2 focus:ring-[#9b1c2e]/10 text-gray-800 text-sm bg-white transition-all placeholder-gray-400 resize-none"
                  />
                </div>

                {/* Consent Checkbox */}
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.agree}
                    onChange={(e) => setForm({ ...form, agree: e.target.checked })}
                    className="mt-1 accent-[#9b1c2e] h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-500 leading-normal">
                    I agree to receive messages over RCS/WhatsApp/SMS
                  </span>
                </label>

                {/* Google reCAPTCHA */}
                <div className="pt-1">
                  <ReCaptcha
                    ref={captchaRef}
                    theme="light"
                    onChange={(token) => {
                      setCaptchaToken(token);
                      if (token) setCaptchaError("");
                    }}
                    onExpired={() => setCaptchaToken("")}
                    errorMessage={captchaError}
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-12 py-4 bg-[#9b1c2e] hover:bg-[#7d1624] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold tracking-widest uppercase rounded-xl transition-colors duration-200 flex items-center justify-center gap-3 shadow-lg shadow-[#9b1c2e]/20"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth={4}
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

        </div>
      </section>

      <FAQSection faqs={contactFaqs} />
    </div>
  );
};

export default ContactPage;

